import React, { useState, useEffect } from 'react';
import {
    Box,
    Text,
    Input,
    Textarea,
    Grid,
    GridItem,
    Button,
    HStack
} from '@chakra-ui/react';
import {
    DialogRoot,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogTitle,
    DialogCloseTrigger
} from '../../ui/dialog';
import { useColorModeValue } from '../../ui/color-mode';
import { darkMode, lightMode } from '../../../theme/config';
import { dateFormatter } from '../../../utils/dateTime/date';

const ViewData = ({
    isOpen,
    onClose,
    rowData,
    onUpdate,
    serviceRequestCategories = [],
}) => {
    // console.log(rowData)
    const [description, setDescription] = useState('');
    const [sendFeedback, setSendFeedback] = useState(false);
    const [status, setStatus] = useState('');
    const [remarks, setRemarks] = useState('');

    const textColor = useColorModeValue(lightMode.textColor, darkMode.textColor);
    const borderColor = useColorModeValue(lightMode.containerBorderColor, darkMode.containerBorderColor);
    const bgColor = useColorModeValue('#f8f9fa', '#2d3748');

    useEffect(() => {
        if (rowData) {
            setDescription(rowData.description || '');
            setStatus(rowData.status || 'OPEN');
            setRemarks('');
            setSendFeedback(false);
        }
    }, [rowData]);

    const handleUpdate = () => {
        if (onUpdate) {
            onUpdate({
                ...rowData,
                description,
                status,
                remarks,
                sendFeedback
            });
        }
        onClose();
    };

    const getCategoryName = (categoryId) => {
        const category = serviceRequestCategories.find(cat => cat.id === categoryId);
        return category ? category.name : categoryId;
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return dateFormatter(new Date(parseInt(timestamp)));
    };

    const formatWarrantyDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return dateFormatter(new Date(parseInt(timestamp) * 1000));
    };

    return (
        <DialogRoot open={isOpen} onOpenChange={({ open }) => !open && onClose()}>
            <DialogContent maxWidth="60vw" maxHeight="90vh">
                {/* Dialog Header */}
                <DialogHeader>
                    <DialogTitle fontSize="xl" fontWeight="bold" color={textColor}>
                        View Complaint Details
                    </DialogTitle>
                    <DialogCloseTrigger />
                </DialogHeader>

                {/* Dialog Body with scrollable content */}
                <DialogBody overflowY="auto" maxHeight="70vh">
                    <Box p={2}>
                        {/* Main Grid Layout */}
                        <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                            {/* Row 1 */}
                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Complaint ID</Text>
                                <Input
                                    value={`Complaint ID - ${rowData?.id?.slice(-4) || 'N/A'}`}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Priority</Text>
                                <Input
                                    value={rowData?.priority || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            {/* <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Status</Text>
                                <Select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    bg="white"
                                    border={`1px solid ${borderColor}`}
                                >
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem> */}

                            {/* Row 2 - Customer Information */}
                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Customer Name</Text>
                                <Input
                                    value={rowData?.customerName || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Email Id</Text>
                                <Input
                                    value={rowData?.customerEmail || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Contact No.</Text>
                                <Input
                                    value={rowData?.customerContact || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            {/* Row 3 - Product Information */}
                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Product Name</Text>
                                <Input
                                    value={rowData?.productName || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Product Category</Text>
                                <Input
                                    value={getCategoryName(rowData?.productCategory) || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Serial No.</Text>
                                <Input
                                    value={rowData?.productSerial || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            {/* Row 4 - Complaint and Warranty Information */}
                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Complaint Type</Text>
                                <Input
                                    value={getCategoryName(rowData?.complaintCategory) || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Purchase Date</Text>
                                <Input
                                    value={formatWarrantyDate(rowData?.purchaseDate)}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Warranty Status</Text>
                                <Input
                                    value={rowData?.warrantyStatus?.replace('_', ' ') || 'N/A'}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            {/* Row 5 - Date Information */}
                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Warranty Valid Date</Text>
                                <Input
                                    value={formatWarrantyDate(rowData?.warrantyValidDate)}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Created Date</Text>
                                <Input
                                    value={formatDate(rowData?.createdDate)}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>

                            <GridItem>
                                <Text fontSize="sm" color="gray.500" mb={1}>Last Update</Text>
                                <Input
                                    value={formatDate(rowData?.lastUpdatedDate)}
                                    isReadOnly
                                    bg={bgColor}
                                    border={`1px solid ${borderColor}`}
                                />
                            </GridItem>
                        </Grid>

                        {/* Description - Full Width and Editable */}
                        <Box mt={6}>
                            <Text fontSize="sm" color="gray.500" mb={1}>Description</Text>
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Enter complaint description..."
                                minHeight="120px"
                                resize="vertical"
                            />
                        </Box>

                        {/* Assigned To - Full Width */}
                        <Box mt={6}>
                            <Text fontSize="sm" color="gray.500" mb={1}>Assigned To</Text>
                            <Input
                                value={rowData?.assignedTo || ''}
                                isReadOnly
                                bg={bgColor}
                                border={`1px solid ${borderColor}`}
                            />
                        </Box>

                        {/* Remarks - Full Width */}
                        <Box mt={6}>
                            <Text fontSize="sm" color="gray.500" mb={1}>Remarks</Text>
                            <Textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Type your remarks here..."
                                minHeight="80px"
                                resize="vertical"
                            />
                        </Box>

                        {/* Feedback Checkbox */}
                        {/* <Box mt={6}>
                            <Checkbox
                                isChecked={sendFeedback}
                                onChange={(e) => setSendFeedback(e.target.checked)}
                                colorScheme="blue"
                            >
                                <Text fontSize="sm" color={textColor}>
                                    Do you want to send a feedback message to customer?
                                </Text>
                            </Checkbox>
                        </Box> */}
                    </Box>
                </DialogBody>

                {/* Dialog Footer with Action Buttons */}
                <DialogFooter>
                    <HStack spacing={3}>
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button 
                            colorScheme="blue" 
                            onClick={handleUpdate}
                            variant="solid"
                        >
                            Update
                        </Button>
                    </HStack>
                </DialogFooter>
            </DialogContent>
        </DialogRoot>
    );
};

export default ViewData;